import * as Sentry from "@sentry/vue";
import { useAudioSettingStore, AudioName } from "~/stores/audioSettingStore";
import { useInboxStore } from "~/projects/help-desk/stores/inboxStore";
import { useAuthStore } from "~/stores/auth";
import { Ticket, Conversation } from "~/projects/help-desk/types";
import { Task } from "~/projects/task-manager/types/task";
import { useTicketsStore } from "~/projects/help-desk/stores/ticketsStore";
import { useAutoDialerStore } from "~/projects/tele-sales/stores/autoDialerStore";
import { useSettingsStore } from "~/projects/cc/stores/settingsStore";

export const setupAudioStream = (stream: any) => {
  const audioEl = new window.Audio();
  audioEl.srcObject = stream;
  audioEl.autoplay = true;
  return audioEl;
};

const userDefinedLinks = {
  incomingCall: null,
  unhandledCall: null,
  newTicket: null,
  newConv: null,
  newInboxConv: null,
  newSupportMsg: null,
  newTask: null,
  taskReminder: null,
  taskOverdue: null,
};
const fixedLinks = {
  answered: null,
  rejected: null,
  missed: null,
  trying: null,
  failed: null,
};

type SoundName = keyof typeof userDefinedLinks | keyof typeof fixedLinks;

let links = {} as Partial<Record<SoundName, string>>;
let active = {} as Partial<Record<SoundName, boolean>>;
let sounds = {} as Partial<Record<SoundName, HTMLAudioElement>>;

export const init = () => {
  bindEvents();
  initSounds();
};

export const getLinks = () => {
  return links;
};

const initSounds = () => {
  links = {};
  active = {};
  sounds = {};
  const store = useAudioSettingStore();

  // A browser that saved its settings before a sound existed has no entry for
  // it, and the saved copy replaces the defaults rather than merging.
  store.ensureDefaults();
  for (const k in store.user) {
    links[k] = JSON.parse(JSON.stringify(store.user[k].value));
    active[k] = JSON.parse(JSON.stringify(store.user[k].active));
  }
  for (const k in store.fixed) {
    links[k] = store.fixed[k];
    active[k] = true;
  }
  for (const k in links) {
    sounds[k as SoundName] = new Audio(links[k] as string);
    sounds[k as SoundName].crossOrigin = "anonymous";
  }
  sounds.incomingCall.loop = true;
  sounds.trying.loop = true;
  sounds.trying.volume = 0.1;
};

const initDeviceMapping = (
  soundDeviceMap: Partial<Record<SoundName, string>>,
) => {
  for (const k in soundDeviceMap) {
    if (soundDeviceMap[k]) {
      sounds[name as SoundName].setSinkId(soundDeviceMap[k]);
    }
  }
};

let boundEvents = false;
const bindEvents = () => {
  if (boundEvents) return;

  const inboxStore = useInboxStore();
  const authStore = useAuthStore();
  const ticketsStore = useTicketsStore();
  const route = useRoute();
  const { $listen } = useNuxtApp();

  $listen("audio_settings_changed", () => {
    initSounds();
  });
  $listen("uh_call_created", () => {
    play("unhandledCall");
  });
  $listen("ticket_created", (t: Ticket) => {
    if (
      !route.path.includes("help-desk/inbox") &&
      ticketsStore.isTicketForMe(t)
    ) {
      play("newTicket");
    }
  });
  $listen("ticket_unpicked", (t: Ticket) => {
    if (t.agent_id == authStore.user.id) return;
    if (!route.path.includes("help-desk/inbox")) {
      play("newTicket");
    }
  });
  $listen("conv_created", (c: Conversation) => {
    if (c.ticket.agent_id != authStore.user.id) return;
    if (
      route.path.includes("help-desk/inbox") &&
      inboxStore.selectedTicket?.id == c.ticket_id
    ) {
      play("newInboxConv");
    } else {
      play("newConv");
    }
  });
  $listen("task_created", (t: Task) => {
    if (t.user_id == authStore.user.id) return;
    play("newTask");
  });
  $listen("task_reminder", (t: Task) => {
    play("taskReminder");
  });
  $listen("task_overdue", (t: Task) => {
    play("taskOverdue");
  });
  boundEvents = true;
};

export const play = (name: SoundName): Promise<void> => {
  if (!useAutoDialerStore().autoDialer) {
    if (name in active && active[name] == false) return Promise.resolve();

    // Nothing to play is not a crash. Without this, one unknown name takes the
    // caller down with it.
    if (!sounds[name]) return Promise.resolve();

    return sounds[name].play().catch((e) => {
      console.error("audio play error for " + name);
      console.error(e);
    });
  }
};
export const pause = (name: SoundName): Promise<void> => {
  if (name in active && active[name] == false) return Promise.resolve();
  sounds[name].pause();
  return Promise.resolve();
};
export const stop = (name: SoundName): Promise<void> => {
  if (name in active && active[name] == false) return Promise.resolve();
  sounds[name].pause();
  sounds[name].currentTime = 0;
  return Promise.resolve();
};

export default {
  setupAudioStream,
  init,
  getLinks,
  play,
  pause,
  stop,
};
